import React, { useMemo, useState } from 'react'
import { capitalCase } from 'change-case'
import {
  Activity,
  AlertTriangle,
  BadgePercent,
  BarChart3,
  Coins,
  DollarSign,
  Gift,
  Heart,
  History,
  LayoutDashboard,
  ListOrdered,
  LucideIcon,
  Menu,
  Network,
  PieChart,
  SlidersHorizontal,
  Table2,
  Target,
  TrendingUp,
  Trophy,
} from 'lucide-react'

import { useMember, useTenant } from '@rewardops/auth'
import {
  Avatar,
  AvatarFallback,
} from '@rewardops/replit/src/components/ui/avatar'
import { useIsDesktop, usePAWStore } from '@rewardops/ui'
import { getComplementColor, lightenColor } from '@rewardops/utils'

import { useApi } from '../hooks/use-api'

interface NavItem {
  title: string
  sectionId: string
  icon: React.ComponentType<{ className?: string }>
  conditional?: boolean
  configKey?:
    | 'showGoals'
    | 'showAICoaching'
    | 'showTraining'
    | 'showDiscretionaryBank'
}

const scrollToSection = (sectionId: string) => {
  const element = document.querySelector(`[data-testid="${sectionId}"]`)

  if (element) {
    const mainContainer = document.querySelector('main')

    const windowContainer = document.querySelector('#paw-main')

    if (mainContainer) {
      const headerOffset = 80
      const elementRect = element.getBoundingClientRect()
      const containerRect = mainContainer.getBoundingClientRect()
      const scrollTop = mainContainer.scrollTop
      const offsetPosition =
        elementRect.top - containerRect.top + scrollTop - headerOffset

      mainContainer.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
    }

    if (windowContainer) {
      const headerOffset = 80
      const elementRect = element.getBoundingClientRect()
      const containerRect = windowContainer.getBoundingClientRect()
      const scrollTop = windowContainer.scrollTop
      const offsetPosition =
        elementRect.top - containerRect.top + scrollTop - headerOffset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
    }
  }
}

const navigateToSection = (sectionId: string) => {
  scrollToSection(sectionId)
}

const pawDashboardIconsMap: Record<string, LucideIcon> = {
  tier_progress: TrendingUp,
  promotions: BadgePercent,
  performance: Activity,
  leaderboard: Trophy,
  earning_history: History,
  rewards_catalog: Gift,
  points_balance: Coins,
  pending_manual_adjustments: SlidersHorizontal,
  sales: DollarSign,
  rankings: ListOrdered,
  overview: LayoutDashboard,
  goals: Target,
  engagement: Heart,
  comparison_table: Table2,
  tier_distribution: PieChart,
  needs_attention: AlertTriangle,
  comparison_charts: BarChart3,
  heirarchy: Network,
}

export function PAWDashboardSidebar(props?: { role?: string; content: any }) {
  const member = useMember()
  const { tier } = usePAWStore((state) => state)
  const tenant = useTenant()
  const { api } = useApi()
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false)

  const { data: subtitle } = api.pawDashboards.getUserSubtitle.useQuery()

  const isDesktop = useIsDesktop()

  const userRole = member?.role ?? ''

  const contentfulSections: NavItem[] = useMemo(() => {
    return props?.content?.sections?.map(
      (section: { sectionId: string; title?: string }) => ({
        title: section?.title ?? section?.sectionId?.replace(/_/g, ' '),
        icon: pawDashboardIconsMap?.[section?.sectionId] ?? null,
        sectionId: section?.sectionId,
      }),
    )
  }, [props?.content?.sections])

  const getInitials = (str: string) => {
    const splittedInitials = str
      ?.split(' ')
      ?.map((name: string) => name?.charAt(0)?.toUpperCase())

    return splittedInitials?.join('')
  }

  const getMemberDesignation = () => {
    return capitalCase(userRole)?.replace('_', ' ')
  }

  const gradientColor = getComplementColor(
    tenant?.customStyling?.primaryColor ?? '#FFFFFF',
  )

  const hoverColor = lightenColor(
    tenant?.customStyling?.primaryColor ?? '#FFFFFF',
    60,
  )

  const sidebarContent = (
    <React.Fragment>
      <div
        className="flex items-center justify-center gap-3 p-5 md:justify-start"
        data-testid="sidebar-profile"
      >
        <Avatar className={'h-12 w-12'}>
          <AvatarFallback
            className={`bg-gradient-to-r from-tenant-primary-color to-[var(--gradient-to)] text-primary-foreground`}
            style={
              {
                '--gradient-to': gradientColor,
              } as React.CSSProperties
            }
          >
            {getInitials(
              `${member?.firstName} ${member?.lastName ?? ''}`?.trim(),
            )}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 md:flex-1">
          <p className="truncate font-semibold" data-testid="sidebar-user-name">
            {`${member?.firstName} ${member?.lastName ?? ''}`?.trim()}
          </p>
          <p
            className="truncate text-xs text-muted-foreground"
            data-testid="sidebar-user-role"
          >
            {getMemberDesignation()}
          </p>
          {subtitle ? (
            <p className="truncate text-xs capitalize text-tenant-primary-color">
              {capitalCase(subtitle)}
            </p>
          ) : null}
        </div>
      </div>
      <hr />
      <div className="p-2">
        {contentfulSections.map((item, index) => (
          <div
            data-testid={`nav-${item.sectionId}`}
            key={`${item.sectionId}-${index + 1}`}
          >
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault()
                navigateToSection(item.sectionId)
              }}
              className={`flex w-full gap-2 rounded
                p-2
                hover:bg-[var(--hover-color)] hover:font-semibold hover:text-tenant-primary-color`}
              style={
                {
                  '--hover-color': hoverColor,
                } as React.CSSProperties
              }
            >
              {item?.icon && <item.icon className="h-4 w-4" />}
              <span className="text-sm capitalize">{item.title}</span>
            </a>
          </div>
        ))}
      </div>
    </React.Fragment>
  )

  if (!isDesktop) {
    return (
      <div className="text-center">
        <button
          onClick={() => setSidebarOpen((prev) => !prev)}
          className=""
          aria-label="Open paw dashboard sidebar menu"
        >
          <Menu className="h-6 w-6 text-tenant-primary-color" />
        </button>
        {sidebarOpen ? sidebarContent : null}
      </div>
    )
  }

  return (
    <aside
      id="paw-dashboard-sidebar"
      data-testid="paw-dashboard-sidebar"
      className="sticky top-20 z-30 h-screen w-64 border-r"
    >
      {sidebarContent}
    </aside>
  )
}
